import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrand } from './useBrand';
import { useAuth } from './useAuth';

export interface TwilioPhoneNumber {
  id: string;
  brand_id: string;
  phone_number: string;
  twilio_sid: string;
  friendly_name: string | null;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
  assigned_user_id: string | null;
  assigned_user?: { id: string; full_name: string | null; email: string } | null;
  status: 'active' | 'released' | 'suspended';
  created_at: string;
  updated_at: string;
}

export interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string;
  region: string;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
}

async function callTwilioNumbers(body: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const resp = await supabase.functions.invoke('twilio-numbers', {
    body,
  });

  if (resp.error) throw new Error(resp.error.message);
  return resp.data;
}

export function useTwilioNumbers() {
  const { activeBrandId } = useBrand();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['twilio-numbers', activeBrandId],
    queryFn: () => callTwilioNumbers({ action: 'list', brandId: activeBrandId }),
    enabled: !!user?.id && !!activeBrandId,
    staleTime: 30_000,
  });
}

export interface SearchNumbersParams {
  areaCode?: string;
  country?: string;
  contains?: string;
  matchType?: 'starts_with' | 'ends_with' | 'anywhere';
  voiceEnabled?: boolean;
  smsEnabled?: boolean;
  mmsEnabled?: boolean;
}

export function useSearchAvailableNumbers() {
  const { activeBrandId } = useBrand();

  return useMutation({
    mutationFn: (params: SearchNumbersParams) =>
      callTwilioNumbers({ action: 'search', brandId: activeBrandId, ...params }),
  });
}

export function useBuyNumber() {
  const queryClient = useQueryClient();
  const { activeBrandId } = useBrand();

  return useMutation({
    mutationFn: (params: { phoneNumber: string; friendlyName?: string }) =>
      callTwilioNumbers({ action: 'buy', brandId: activeBrandId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
    },
  });
}

export function useReleaseNumber() {
  const queryClient = useQueryClient();
  const { activeBrandId } = useBrand();

  return useMutation({
    mutationFn: (numberId: string) =>
      callTwilioNumbers({ action: 'release', brandId: activeBrandId, numberId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
    },
  });
}

export function useAssignNumber() {
  const queryClient = useQueryClient();
  const { activeBrandId } = useBrand();

  return useMutation({
    mutationFn: (params: { numberId: string; userId: string | null }) =>
      callTwilioNumbers({ action: 'assign', brandId: activeBrandId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
    },
  });
}

export function useTransferNumber() {
  const queryClient = useQueryClient();
  const { activeBrandId } = useBrand();

  return useMutation({
    mutationFn: (params: { numberId: string; targetBrandId: string }) =>
      callTwilioNumbers({ action: 'transfer', brandId: activeBrandId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
    },
  });
}

export function useUpdateNumberLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { numberId: string; friendlyName: string | null }) => {
      const { error } = await supabase
        .from('twilio_phone_numbers')
        .update({ friendly_name: params.friendlyName })
        .eq('id', params.numberId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] });
    },
  });
}
